import { CartUnavailableProduct, CartProductCard } from "@core/components";
import { Product } from "@core/types";
import { IonSkeletonText, useIonViewWillEnter, useIonViewWillLeave } from "@ionic/react";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { useCartData } from "../../../providers/cartDataStore";
import { useConfig } from "../../../providers/config";
import styles from "./CartForm.module.scss";
import { useActiveOrMainShippingZone } from "../../../providers/shippingZones";
import { useGetIncludeProductsQuery } from "../../../providers/products";
import classNames from "classnames/bind";

const cx = classNames.bind(styles);
const cartProductsSkeleton = [...Array(3).keys()];

export const CartProducts = () => {
  const config = useConfig();
  const [shouldCheckProducts, setShouldCheckProducts] = useState(false);

  const { activeShippingZone, isLoading: isActiveShippingZoneLoading } = useActiveOrMainShippingZone();
  const activeShippingZoneId = activeShippingZone.id;

  const getIncludeProductsEnabled = shouldCheckProducts && !!activeShippingZoneId;

  const {
    cartProducts,
    removeUnavailableProduct,
    replaceCartProducts,
    removeSoldOutProduct,
    soldOutProducts,
    unavailableProducts,
  } = useCartData();

  const { data: products, isLoading: isProductsLoading } = useGetIncludeProductsQuery({
    include: cartProducts.map((product) => String(product.id)),
    params: {
      zoneId: activeShippingZoneId,
    },
    options: {
      enabled: getIncludeProductsEnabled,
    },
  });

  useEffect(() => {
    if (products) {
      replaceCartProducts(products);
      setShouldCheckProducts(false);
    }
  }, [products]);

  useIonViewWillEnter(() => {
    if (cartProducts) {
      setShouldCheckProducts(true);
    }
  }, [cartProducts]);

  useIonViewWillLeave(() => {
    setShouldCheckProducts(false);
  });

  const showProductsLoading = getIncludeProductsEnabled && isProductsLoading && isActiveShippingZoneLoading;

  return (
    <div className={cx("cart_form", "flex flex-col border-solid border-b-1 border-gray-200")}>
      {showProductsLoading ? (
        <div className="flex flex-col gap-2">
          {cartProductsSkeleton.map((_, index) => (
            <div key={index} className="flex items-end justify-between w-full p-2.5">
              <div className="flex items-center gap-1">
                <IonSkeletonText animated className="w-[3.75rem] h-[3.75rem] rounded-full m-0" />
                <div className="flex flex-col gap-2.5">
                  <IonSkeletonText animated className="w-[12rem] m-0" />
                  <IonSkeletonText animated className="w-[6.5625rem] h-[2.0625rem] rounded-full m-0" />
                </div>
              </div>
              <IonSkeletonText animated className="w-[3.125rem] h-[1.5rem] rounded-full m-0" />
            </div>
          ))}
        </div>
      ) : (
        <>
          {config.isUnavailableProductsEnabled && (
            <>
              {unavailableProducts.map((unavailableProduct) => {
                return (
                  <CartUnavailableProduct
                    key={unavailableProduct.id}
                    cartItem={unavailableProduct}
                    product={unavailableProduct as unknown as Product}
                    removeUnavailableProduct={removeUnavailableProduct}
                    removeProductCardTxt={t("Not available in your delivery area")}
                  />
                );
              })}

              {soldOutProducts.map((soldOutProduct) => {
                return (
                  <CartUnavailableProduct
                    key={soldOutProduct.id}
                    cartItem={soldOutProduct}
                    removeProductCardTxt={t("Sold out")}
                    product={soldOutProduct as unknown as Product}
                    removeUnavailableProduct={removeSoldOutProduct}
                  />
                );
              })}
            </>
          )}

          {cartProducts.map((cartItem) => {
            return <CartProductCard key={cartItem.id} cartItem={cartItem} product={cartItem as unknown as Product} />;
          })}
        </>
      )}
    </div>
  );
};
